import type { AudioManager } from '../../audio/AudioManager';
import type { TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawSilhouette, type SilhouetteId } from '../../render/SilhouetteRenderer';
import { drawButton, pointInRect } from '../../render/ui/UI';
import type { Hero, Rect, RunState } from '../../types';
import type { Phase } from '../Phase';
import { tierFor, tierLabel } from '../systems/Reputation';

const BEGIN_BUTTON: Rect = { x: 60, y: 560, w: LOGICAL_WIDTH - 120, h: 56 };

export class HeroRequestPhase implements Phase {
  private hero: Hero;
  private run: RunState;
  private audio: AudioManager;
  private done = false;
  private unbind: (() => void) | null = null;
  private elapsed = 0;
  private entered = 0;

  constructor(hero: Hero, run: RunState, input: TouchInput, audio: AudioManager) {
    this.hero = hero;
    this.run = run;
    this.audio = audio;
    this.unbind = input.onPointerDown((e) => {
      if (pointInRect(e.pos.x, e.pos.y, BEGIN_BUTTON) && this.entered >= 1) {
        this.audio.play('bellows_tap');
        this.done = true;
      }
    });
  }

  update(dt: number): void {
    this.elapsed += dt;
    this.entered = Math.min(1, this.elapsed / 0.6);
  }

  isComplete(): boolean {
    return this.done;
  }

  dispose(): void {
    this.unbind?.();
    this.unbind = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    // Background: warm forge ambience.
    const grad = ctx.createLinearGradient(0, 0, 0, LOGICAL_HEIGHT);
    grad.addColorStop(0, '#1a0f08');
    grad.addColorStop(0.5, '#241608');
    grad.addColorStop(1, '#0e0905');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    // Header.
    ctx.fillStyle = '#e6d3a4';
    ctx.font = 'bold 18px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('A customer arrives at your forge...', LOGICAL_WIDTH / 2, 28);

    // Hero silhouette, sliding in from the right.
    const targetX = LOGICAL_WIDTH / 2;
    const startX = LOGICAL_WIDTH + 80;
    const heroX = startX + (targetX - startX) * easeOutCubic(this.entered);
    drawSilhouette(ctx, this.hero.silhouetteId as SilhouetteId, {
      cx: heroX,
      feetY: 360,
      flip: false,
      swayPhase: this.elapsed * 1.5,
    });

    // Name plate.
    ctx.fillStyle = '#f0d890';
    ctx.font = 'bold 22px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${this.hero.name} ${this.hero.title}`, LOGICAL_WIDTH / 2, 380);

    ctx.fillStyle = '#a8957a';
    ctx.font = '13px system-ui, sans-serif';
    ctx.fillText(this.requestLine(), LOGICAL_WIDTH / 2, 412);

    ctx.fillStyle = '#7a6a55';
    ctx.font = 'italic 12px system-ui, sans-serif';
    ctx.fillText(`"${this.hero.flavorLine}"`, LOGICAL_WIDTH / 2, 436);

    // Hero stats.
    ctx.fillStyle = '#6a5a45';
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillText(
      `Vigor: ${this.hero.maxHp}   ·   ${this.hero.urgency === 'rushed' ? 'In a hurry' : 'Patient'}`,
      LOGICAL_WIDTH / 2,
      460,
    );

    // Run status badge (rep tier + deaths).
    this.drawRepBadge(ctx);
    this.drawDeathCounter(ctx);

    drawButton(ctx, {
      rect: BEGIN_BUTTON,
      label: this.entered >= 1 ? 'BEGIN FORGING' : '...',
      enabled: this.entered >= 1,
      highlight: this.entered >= 1,
    });
  }

  private requestLine(): string {
    const weaponLabel =
      this.hero.weaponPreference === 'sword'
        ? 'a sword'
        : this.hero.weaponPreference === 'axe'
          ? 'an axe'
          : 'a spear';
    return `"Forge me ${weaponLabel}, smith. I will pay well."`;
  }

  private drawRepBadge(ctx: CanvasRenderingContext2D): void {
    const tier = tierFor(this.run.reputation);
    const color =
      tier === 'high' ? '#ffd84a' : tier === 'mid' ? '#c9b896' : '#a06a40';
    ctx.fillStyle = color;
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`Renown: ${tierLabel(tier)} (${this.run.reputation})`, 16, 8);
    ctx.textAlign = 'right';
    ctx.fillText(`Gold: ${this.run.gold}`, LOGICAL_WIDTH - 16, 8);
  }

  private drawDeathCounter(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#c46a3a';
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`Heroes lost: ${this.run.deaths} / 3`, LOGICAL_WIDTH / 2, 8);
  }
}

function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}
